import { ParameterType } from '@/study-search/enums';
import { BotHelper } from '@/study-search/utils/BotHelper';
import { LocationQueryRaw, RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
import { DisciplineFilterDefinition } from '../models/definition';
import { DisciplineFilterOptionInformation } from '@studyportals/search-filters';
import store from '@/study-search/store';

class RoutingDefinitions {
	public readonly ROOT = 'Root';
	public readonly PORTAL_ROUTE = 'Portal';
	public readonly DISCIPLINE_DEGREE_TYPE_ROUTE = 'DisciplineDegreeType';
	public readonly DISCIPLINE_DELIVERY_METHOD_ROUTE = 'DisciplineDeliveryMethod';
	public readonly DISCIPLINE_DURATION_ROUTE = 'DisciplineDuration';
	public readonly DISCIPLINE_FORMAT_ROUTE = 'DisciplineAttendance';
	public readonly DISCIPLINE_TUITION_FEE_ROUTE = 'DisciplineTuitionFee';
	public readonly DISCIPLINE_TUITION_FEE_DELIVERY_METHOD_ROUTE = 'DisciplineTuitionFeeDeliveryMethod';
	public readonly DISCIPLINE_ROUTE = 'Discipline';
	public readonly DISCIPLINE_COUNTRY_ROUTE = 'DisciplineCountry';
	public readonly DISCIPLINE_EDUCATIONAL_FORM_ROUTE = 'DisciplineEducationalForm';
	public readonly DISCIPLINE_EDUCATIONAL_FORM_COUNTRY_ROUTE = 'DisciplineEducationalFormCountry';
	public readonly COUNTRY_DISCIPLINE_ROUTE = 'CountryDiscipline';
	public readonly DISCIPLINE_COUNTRY_AREA_ROUTE = 'DisciplineCountryArea';
	public readonly DISCIPLINE_AREA_ROUTE = 'DisciplineArea';
	public readonly DISCIPLINE_CONTINENT_ROUTE = 'DisciplineContinent';
	public readonly DISCIPLINE_DEGREE_TYPE_COUNTRY_ROUTE = 'DisciplineDegreeTypeCountry';
	public readonly COUNTRY_ROUTE = 'Country';
	public readonly AREA_ROUTE = 'Area';
	public readonly CONTINENT_ROUTE = 'Continent';
	public readonly DEGREE_ROUTE = 'Degree';
	public readonly COUNTRY_DEGREE_TYPE_ROUTE = 'CountryDegreeType';
	public readonly COUNTRY_ATTENDANCE_DEGREE_TYPE_ROUTE = 'CountryAttendanceDegreeType';
	public readonly COUNTRY_DEGREE_TYPE_DURATION_ROUTE = 'CountryDegreeTypeDuration';
	public readonly COUNTRY_DURATION_ROUTE = 'CountryDuration';
	public readonly COUNTRY_ATTENDANCE_ROUTE = 'CountryAttendance';
	public readonly COUNTRY_FORMAT_ROUTE = 'CountryFormat';
	public readonly COUNTRY_TUITION_FEE_ROUTE = 'CountryTuitionFee';
	public readonly COUNTRY_AREA_ROUTE = 'CountryArea';
	public readonly COUNTRY_EDUCATIONAL_FORM_ROUTE = 'CountryEducationalForm';
	public readonly ERROR = 'Error';
	public readonly ATTENDANCE_ROUTE = 'Attendance';
	public readonly ATTENDANCE_DEGREE_TYPE_ROUTE = 'AttendanceDegreeType';
	public readonly SPECIAL_PROGRAMMES_ROUTE = 'SpecialProgrammes';
	public readonly TUITION_FEE_ROUTE = 'TuitionFee';
	public readonly TUITION_FEE_CONTINENT_ROUTE = 'TuitionFeeContinentRoute';
	public readonly DEGREE_TYPE_DELIVERY_METHOD_DISCIPLINE_ROUTE = 'DegreeTypeDeliveryMethodDiscipline';
	public readonly DEGREE_TYPE_COUNTRY_TUITION_FEE_ROUTE = 'DegreeTypeCountryTuitionFee';
	public readonly DEGREE_TYPE_COUNTRY_DELIVERY_METHOD_ROUTE = 'DegreeTypeCountryDeliveryMethod';
	public readonly FORMAT_ROUTE = 'Format';
	public readonly DEGREE_FORMAT_ROUTE = 'DegreeTypeFormat';
	public readonly DEGREE_TUITION_FEE_DELIVERY_METHOD_ROUTE = 'DegreeTypeTuitionFeeDeliveryMethod';
	public readonly DEGREE_AREA_ROUTE = 'DegreeTypeArea';
	public readonly DEGREE_TUITION_FEE_ROUTE = 'DegreeTypeTuitionFee';
	public readonly DEGREE_CONTINENT_ROUTE = 'DegreeTypeContinent';
	public readonly TUITION_FEE_DELIVERY_METHOD_ROUTE = 'TuitionFeeDeliveryMethod';
	public readonly DURATION_ROUTE = 'Duration';
	public readonly DELIVERY_METHOD_DURATION_ROUTE = 'DeliveryMethodDuration';
	public readonly DEGREE_TUITION_FEE_AREA_ROUTE = 'DegreeTuitionFeeArea';
	public readonly DELIVERY_METHOD_DISCIPLINE_CONTINENT_ROUTE = 'DeliveryMethodDisciplineContinent';
	public readonly DEGREE_TYPE_TUITION_FEE_CONTINENT_ROUTE = 'DegreeTypeTuitionFeeContinent';
	public readonly FORMAT_DELIVERY_METHOD_ROUTE = 'FormatDeliveryMethod';
	public readonly DELIVERY_METHOD_CONTINENT_ROUTE = 'DeliveryMethodContinent';
	public readonly FORMAT_CONTINENT_ROUTE = 'FormatContinentRoute';
	public readonly DEGREE_TYPE_DURATION_ROUTE = 'DegreeTypeDuration';
	public readonly SPECIAL_PROGRAMMES_CONTINENT_ROUTE = 'SpecialProgrammesContinent';
	public readonly SPECIAL_PROGRAMMES_COUNTRY_ROUTE = 'SpecialProgrammesCountry';
	public readonly FORMAT_ATTENDANCE_ROUTE = 'FormatAttendance';
	public readonly ATTENDANCE_AREA_ROUTE = 'AttendanceArea';
	public readonly DEGREE_TYPE_TUITION_FEE_FORMAT_ROUTE = 'DegreeTypeTuitionFeeFormat';
	public readonly DURATION_CONTINENT_ROUTE = 'DurationContinent';
	public readonly SPECIAL_PROGRAMMES_DISCIPLINE_ROUTE = 'SpecialProgrammesDiscipline';
	public readonly DEGREE_TYPE_FORMAT_ATTENDANCE_ROUTE = 'DegreeTypeFormatAttendance';
	public readonly ATTENDANCE_DISCIPLINE_COUNTRY_ROUTE = 'AttendanceDisciplineCountry';
	public readonly FORMAT_DISCIPLINE_COUNTRY_ROUTE = 'FormatDisciplineCountry';
	public readonly DEGREE_TYPE_SPECIAL_PROGRAMMES_ROUTE = 'DegreeTypeSpecialProgrammes';
	public readonly DEGREE_TYPE_SPECIAL_PROGRAMMES_ATTENDANCE_ROUTE = 'DegreeTypeSpecialProgrammesAttendance';

	private readonly botHelper = new BotHelper();

	public stringifyQuery(this: void, query: LocationQueryRaw): string {
		if (Object.keys(query).length > 0) {
			const mappedQueryParameters = Object.keys(query).map((key) => {
				return `${key.toString()}=${query[key]}`;
			});
			const queryString = mappedQueryParameters.join('&');

			return `${queryString}`;
		}

		return '';
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public beforeEnter = async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext): Promise<void> => {
		const portalPath = store.getters.getPortalPath();
		if (to.params.portal_type && to.params.portal_type !== portalPath) {
			next(`/${portalPath}`);
			return;
		}
		next();
	};

	public beforeEnterWithDisciplineRouting = async (
		to: RouteLocationNormalized,
		_from: RouteLocationNormalized,
		next: NavigationGuardNext,
	): Promise<void> => {
		const portalPath = store.getters.getPortalPath();
		if (to.params.portal_type && to.params.portal_type !== portalPath) {
			next(`/${store.getters.getPortalPath()}`);
		} else if (this.isSynonym(to.params.discipline as string) && !this.botHelper.isBot() && !this.botHelper.isPrerender()) {
			const filterInformation = this.getDisciplineFilterOptionInformation();
			const disciplineOption = filterInformation.find((_) => _.synonym === to.params.discipline);
			next({
				name: to.name?.toString(),
				params: {
					...to.params,
					discipline: disciplineOption?.optionPath,
				},
				query: to.query,
			});
		} else {
			next();
		}
	};

	public afterEach = (to: RouteLocationNormalized): void => {
		const navigationFailed = to.matched.length === 0;
		if (navigationFailed) {
			return;
		}

		if (
			this.isRoutingDiscipline(to.name as string) &&
			this.isSynonym(to.params.discipline as string) &&
			(this.botHelper.isBot() || this.botHelper.isPrerender())
		) {
			const filterInformation = this.getDisciplineFilterOptionInformation();
			const disciplineOption = filterInformation.find((_) => _.synonym === to.params.discipline);

			const location = to.fullPath.replace(to.params.discipline as string, disciplineOption?.optionPath as string);
			store.mutations.setMetaRefreshInfo(`/search${location}`);
			return;
		}
		store.mutations.setMetaRefreshInfo('');
	};

	private getDisciplineFilterOptionInformation(): DisciplineFilterOptionInformation[] {
		return store.getters.getFilterOptionInformation(ParameterType.DISCIPLINES) as DisciplineFilterOptionInformation[];
	}

	private isSynonym(term: string): boolean {
		const synonyms = DisciplineFilterDefinition.getSynonyms();
		return synonyms.includes(term);
	}

	private isRoutingDiscipline = (term: string): boolean => {
		return term.indexOf(this.DISCIPLINE_ROUTE) !== -1;
	};
}

export default new RoutingDefinitions();
